import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";

/**
 * LogoutForm - Logout confirmation component replacing account/logout.html
 *
 * This component provides:
 * - Logout confirmation
 * - Automatic logout execution
 * - Loading states
 * - Redirect after logout
 */
const LogoutForm: React.FC = () => {
  const { logout, authState, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
  };

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        py: { xs: 2, sm: 4 },
        minWidth: 0,
      }}
    >
      <Card>
        <CardContent sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            Sign Out
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Are you sure you want to sign out of your account?
          </Typography>

          {/* Error Display */}
          {authState.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {authState.error}
            </Alert>
          )}

          {/* Success Display */}
          {authState.success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {authState.success}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", flexWrap: 'wrap', gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/"
              disabled={authState.isLoading}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={handleLogout}
              disabled={authState.isLoading}
            >
              {authState.isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign Out"
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Link
              component={RouterLink}
              to="/"
              variant="body2"
              color="primary"
              sx={{ textDecoration: "none" }}
            >
              Return to Home
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LogoutForm;
